import { useState } from "react";
import { Alert, Box, Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "i18n-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { splitEmailAddresses } from "../lib/splitEmailAddresses";

// Card #3610 -- staff edit a rider's "Scoresheet recipients" (GLOSSARY.md) for
// one show: change the address FVD sent and add more. Every scoresheet that
// rider gets at the show (auto-send on class placed, and Resend) goes to the
// list. Saved on the rider's person row by score-api's
// PUT /show/:showId/riders/:riderId/scoresheet-recipients; FVD reconcile never
// touches it and nothing is written back to FVD.
//
// `rider` is the score-person row from GET /show/:showId/riders. The staff
// list for this show is rider.scoresheetRecipients[showId]; with none, the
// list starts as FVD's address(es). Mount with a `key` of the rider id so a
// different rider starts from their own list.

// score-api failure code -> static i18n key (never render a raw backend
// message). The fix is always the same button the staff already see: edit the
// list and press Save again.
const saveErrorKey = (code, data) => {
  if (code === "validation_error" && data?.max) return "people.recipients.tooMany";
  if (code === "not_found") return "people.recipients.notInShow";
  return "people.recipients.saveError";
};

export function staffRecipientsFor(rider, showId) {
  const list = rider?.scoresheetRecipients?.[showId];
  return Array.isArray(list) ? list.filter(Boolean) : [];
}

export default function ScoresheetRecipientsEditor({ showId, rider, onSaved }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const staffList = staffRecipientsFor(rider, showId);
  const fvdList = splitEmailAddresses(rider?.email);
  const startList = staffList.length ? staffList : fvdList;

  const [rows, setRows] = useState(startList.length ? startList : [""]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const cleaned = splitEmailAddresses(rows.join(" "));
  const dirty = cleaned.join("\n") !== startList.join("\n");

  const save = async (emails) => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const res = unwrap(await scoreApi.people.setScoresheetRecipients(showId, rider.id, { emails }));
      const next = res?.emails ?? emails;
      onSaved?.(rider.id, next);
      setRows(next.length ? next : fvdList.length ? fvdList : [""]);
      setSaved(true);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const errData = error?.response?.data;
  const invalid = errData?.code === "validation_error" ? errData?.data?.invalid : null;
  const errorKey = error ? saveErrorKey(errData?.code, errData?.data) : null;

  return (
    <Box data-testid="scoresheet-recipients" sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom color="text.primary">
        {t("people.recipients.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t("people.recipients.help")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-testid="recipients-source">
        {staffList.length ? t("people.recipients.staffSource") : t("people.recipients.fvdSource")}
      </Typography>

      <Stack spacing={1.5}>
        {rows.map((value, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              type="email"
              label={t("people.recipients.emailLabel")}
              placeholder={fvdList.length ? "" : t("people.recipients.noneOnFile")}
              value={value}
              inputProps={{ "data-testid": `recipient-input-${i}` }}
              onChange={(e) => {
                setSaved(false);
                setRows((prev) => prev.map((v, j) => (j === i ? e.target.value : v)));
              }}
            />
            {rows.length > 1 && (
              <IconButton
                aria-label={t("people.recipients.remove")}
                data-testid={`recipient-remove-${i}`}
                onClick={() => {
                  setSaved(false);
                  setRows((prev) => prev.filter((_, j) => j !== i));
                }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            )}
          </Box>
        ))}
      </Stack>

      <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button
          size="small"
          data-testid="recipient-add"
          onClick={() => {
            setSaved(false);
            setRows((prev) => [...prev, ""]);
          }}
        >
          {t("people.recipients.add")}
        </Button>
        <Button
          variant="contained"
          size="small"
          loading={saving}
          disabled={!dirty || saving || cleaned.length === 0}
          data-testid="recipients-save"
          onClick={() => save(cleaned)}
        >
          {t("people.recipients.save")}
        </Button>
        {staffList.length > 0 && (
          <Button
            color="inherit"
            size="small"
            disabled={saving}
            data-testid="recipients-use-fvd"
            onClick={() => save([])}
          >
            {t("people.recipients.useFvd")}
          </Button>
        )}
      </Box>

      {invalid && (
        <Alert severity="error" sx={{ mt: 2 }} data-testid="recipients-error">
          {t("people.recipients.invalidEmail", { addresses: invalid.join(", ") })}
        </Alert>
      )}
      {error && !invalid && (
        <Alert severity="error" sx={{ mt: 2 }} data-testid="recipients-error">
          {t(errorKey || "people.recipients.saveError")}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mt: 2 }} data-testid="recipients-saved">
          {t("people.recipients.saved")}
        </Alert>
      )}
    </Box>
  );
}
